import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrandLogo } from '../components/BrandLogo';
import { apiPublic } from '../lib/api';

type EarlyBirdStatus = {
  totalSlots: number;
  claimed: number;
  remaining: number;
  promoCode: string | null;
  offerLabel: string;
};

export function LandingPage() {
  const [early, setEarly] = useState<EarlyBirdStatus | null>(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref')?.trim();
      if (ref) {
        void import('../lib/influencerTracking').then(({ trackRefVisit }) => {
          void trackRefVisit(ref, 'landing');
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void apiPublic<EarlyBirdStatus>('/api/auth/early-bird')
      .then(setEarly)
      .catch(() => setEarly(null));
  }, []);

  return (
    <div className="landing-root min-h-screen overflow-x-hidden text-zinc-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Manrope:wght@400;500;600;700&display=swap');
        .landing-root {
          font-family: Manrope, system-ui, sans-serif;
          background:
            radial-gradient(900px 480px at 12% -8%, rgba(56, 189, 248, 0.2), transparent 55%),
            radial-gradient(700px 420px at 100% 8%, rgba(250, 204, 21, 0.08), transparent 50%),
            linear-gradient(165deg, #070a12 0%, #0c1220 45%, #081018 100%);
        }
        .landing-display { font-family: Syne, Manrope, sans-serif; }
      `}</style>

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:gap-4 sm:px-8 sm:py-5">
        <Link to="/" className="flex min-w-0 items-center gap-2.5" aria-label="Peak Performance IQ home">
          <BrandLogo heightClass="h-8 w-8 shrink-0 sm:h-9 sm:w-9" />
          <span className="landing-display hidden truncate text-sm font-bold tracking-wide text-white sm:inline sm:text-base">
            Peak Performance IQ
          </span>
        </Link>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <Link
            to="/login"
            className="rounded-full px-2.5 py-2 text-xs font-medium text-zinc-300 transition hover:text-white sm:px-3 sm:text-sm"
          >
            Sign in
          </Link>
          <Link
            to="/signup?founders=1"
            className="rounded-full bg-sky-400 px-3 py-2 text-xs font-semibold text-zinc-950 shadow-[0_8px_24px_rgba(56,189,248,0.3)] transition hover:bg-sky-300 sm:px-4 sm:text-sm"
          >
            <span className="sm:hidden">Join</span>
            <span className="hidden sm:inline">Become a founder</span>
          </Link>
        </div>
      </header>

      <main>
        <section className="relative mx-auto flex min-h-[calc(100dvh-4.5rem)] max-w-6xl flex-col justify-center px-4 pb-14 pt-4 sm:min-h-[calc(100dvh-5rem)] sm:px-8 sm:pb-24 sm:pt-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-3xl"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-300/90 sm:text-xs sm:tracking-[0.22em]">
              Founding members
            </p>
            <h1 className="landing-display mt-3 max-w-full text-[clamp(1.75rem,8.4vw,4.5rem)] font-extrabold leading-[1.05] tracking-tight text-white sm:mt-4 sm:text-[clamp(2.35rem,5vw,4.25rem)]">
              <span className="block md:inline">Peak</span>
              <span className="hidden md:inline"> </span>
              <span className="block md:inline">Performance</span>
              <span className="hidden md:inline"> </span>
              <span className="block md:inline">IQ</span>
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-zinc-400 sm:mt-5 sm:text-lg">
              Training, nutrition, recovery, and AI coaching in one place — built for athletes who want
              clarity, not another cluttered tracker.
            </p>
            <div className="mt-7 flex w-full flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center">
              <Link
                to="/signup?founders=1"
                className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-zinc-950 transition hover:bg-zinc-200 sm:w-auto sm:py-3"
              >
                Become a founder
              </Link>
              <a
                href="#offer"
                className="inline-flex w-full items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-zinc-100 backdrop-blur transition hover:border-white/30 sm:w-auto sm:py-3"
              >
                See the offer
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.55 }}
            className="mt-10 w-full max-w-xl rounded-2xl border border-white/10 bg-black/30 px-4 py-5 backdrop-blur-md sm:mt-12 sm:px-6 sm:py-6"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-300/80">Become a founder</p>
            <p className="mt-2 text-sm leading-relaxed text-zinc-300 sm:text-base">
              Founding Offer: <strong className="text-white">$7.99/month</strong>, a{' '}
              <strong className="text-white">7-day free trial</strong>, <strong className="text-white">50% off your first paid month</strong>,
              then log <strong className="text-white">25 days</strong> in month 1 for <strong className="text-white">30% off month 2</strong>.
            </p>
            {early?.offerLabel ? (
              <p className="mt-2 text-xs text-zinc-500">{early.offerLabel}</p>
            ) : null}
            <Link
              to="/signup?founders=1"
              className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-sky-400 px-6 py-3 text-sm font-semibold text-zinc-950 transition hover:bg-sky-300 sm:w-auto"
            >
              Become a founder
            </Link>
          </motion.div>
        </section>

        <section className="border-t border-white/10 px-4 py-14 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="landing-display text-2xl font-bold text-white sm:text-4xl">What you get</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              One home for the work that actually moves the needle — logged training, nutrition targets,
              sleep, Apple Health sync, and an AI coach that knows your data.
            </p>
            <div className="mt-8 grid gap-8 sm:mt-10 sm:grid-cols-3">
              {[
                {
                  title: 'Train with intent',
                  body: 'Log workouts, programs, and PRs without fighting the UI. Form check when you need eyes on a set.',
                },
                {
                  title: 'Fuel the plan',
                  body: 'Daily targets, meal logging, and estimates that stay honest about restaurant portions.',
                },
                {
                  title: 'Coach that remembers',
                  body: 'AI coaching grounded in what you logged — progress reviews, recovery calls, and next steps.',
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ delay: i * 0.08 }}
                >
                  <h3 className="landing-display text-lg font-bold text-white sm:text-xl">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="offer" className="border-t border-white/10 px-4 py-14 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="landing-display text-2xl font-bold text-white sm:text-4xl">Become a founder</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              The original early-access deal, still live. Subscribe through <strong className="text-zinc-200">Stripe</strong> at{' '}
              <strong className="text-zinc-200">$7.99/month</strong> with a <strong className="text-zinc-200">7-day free trial</strong> and{' '}
              <strong className="text-zinc-200">50% off the first paid month</strong>. Manually log in the app on{' '}
              <strong className="text-zinc-200">25 days</strong> out of your first month (meals, workouts, water,
              sleep, or a rest day — Apple Health auto-imports don&apos;t count) and{' '}
              <strong className="text-zinc-200">month 2 is automatically 30% off</strong>.
            </p>
            <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-end sm:gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Early list slots</p>
                <p className="landing-display mt-1 text-4xl font-bold text-sky-300">
                  {early ? early.remaining : '—'}
                  <span className="text-lg text-zinc-500"> / {early?.totalSlots ?? 300}</span>
                </p>
              </div>
              <Link
                to="/signup?founders=1"
                className="inline-flex w-full items-center justify-center rounded-full bg-sky-400 px-6 py-3.5 text-sm font-semibold text-zinc-950 transition hover:bg-sky-300 sm:w-auto sm:py-3"
              >
                Become a founder
              </Link>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-zinc-500">
              Peak Performance IQ is built for iPhone. Create your founder account, check out, then download the app from the App Store and sign in with that same account.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] text-sm text-zinc-500 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
          <span className="min-w-0 break-words">
            © {new Date().getFullYear()} Peak Performance&nbsp;IQ
          </span>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-zinc-300">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-zinc-300">
              Terms
            </Link>
            <Link to="/account" className="hover:text-zinc-300">
              Manage billing
            </Link>
            <Link to="/login" className="hover:text-zinc-300">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
